import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";

export default function FormatoCesantias({ onBackToMenu }) {
  const [datosEmpleado, setDatosEmpleado] = useState({
    nombres: '', documento: '', tipoDoc: 'CC', cargo: 'Cargando...', centroTrabajo: ''
  });

  const [formData, setFormData] = useState({
    fechaSolicitud: new Date().toISOString().split('T')[0],
    tipoRetiro: 'PARCIAL', 
    fondoCesantias: '', 
    valorNumeros: '', 
    valorLetras: '', 
    motivo: '1'
  });

  const [archivoSoporte, setArchivoSoporte] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [huellaVerificada, setHuellaVerificada] = useState(false);
  const [selloBiometrico, setSelloBiometrico] = useState(null);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const usuarioLocal = JSON.parse(localStorage.getItem("usuario") || "{}");
    const token = localStorage.getItem("token");

    const fetchPerfilReal = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${API_URL}/api/empleados/${usuarioLocal.documento}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.empleado) {
            setDatosEmpleado({
              nombres: usuarioLocal.nombres || '', 
              documento: usuarioLocal.documento || '',
              tipoDoc: 'CC', 
              cargo: data.empleado.cargo || 'Sin cargo asignado', 
              centroTrabajo: usuarioLocal.centroTrabajo || ''
            });
          }
        }
      } catch (error) {
        setDatosEmpleado({
          nombres: usuarioLocal.nombres || '', 
          documento: usuarioLocal.documento || '',
          tipoDoc: 'CC', 
          cargo: 'Administrador', 
          centroTrabajo: usuarioLocal.centroTrabajo || ''
        });
      }
    };
    if (usuarioLocal.documento) fetchPerfilReal();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); 
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      canvasRef.current.getContext('2d').closePath();
      setIsDrawing(false);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  };

  const capturarHuella = async () => {
    // Validación biométrica interna optimizada para intranet y lectores locales
    Swal.fire({
      title: 'Lector Biométrico',
      text: 'Coloque su dedo en el sensor o presione aceptar para validar identidad...',
      imageUrl: 'https://cdn-icons-png.flaticon.com/512/61/61155.png',
      imageWidth: 70,
      showCancelButton: true,
      confirmButtonText: 'Validar Huella',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        const fechaHora = new Date().toLocaleString();
        const randomHex = Math.random().toString(16).substring(2, 14).toUpperCase();
        const tokenSeguridad = `BIO-AUTH-${randomHex}-${Date.now().toString().slice(-4)}`;

        setSelloBiometrico({
          fecha: fechaHora,
          token: tokenSeguridad,
          usuario: datosEmpleado.documento
        });
        setHuellaVerificada(true);

        Swal.fire({
          icon: 'success',
          title: 'Huella Validada',
          text: 'Identidad confirmada biométricamente.',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Biometría Cancelada',
          text: 'No se completó la lectura de la huella.',
          confirmButtonColor: '#3b82f6'
        });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fondoCesantias || !formData.valorNumeros || !formData.valorLetras) {
      return Swal.fire({ icon: 'warning', title: 'Campos Incompletos', text: 'Diligencie el fondo y los valores a retirar.', confirmButtonColor: '#3b82f6' });
    }

    if (!huellaVerificada) {
      return Swal.fire({ icon: 'error', title: 'Validación Requerida', text: 'Debe validar su identidad biométrica para radicar este formato.', confirmButtonColor: '#ef4444' });
    }

    setLoading(true);
    const firmaBase64 = canvasRef.current?.toDataURL('image/png') || null;

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");

      const formDataEnvio = new FormData();
      formDataEnvio.append('documento', datosEmpleado.documento);
      formDataEnvio.append('nombres', datosEmpleado.nombres);
      formDataEnvio.append('centroTrabajo', datosEmpleado.centroTrabajo);
      formDataEnvio.append('cargo', datosEmpleado.cargo);
      formDataEnvio.append('fechaSolicitud', formData.fechaSolicitud);
      formDataEnvio.append('tipoRetiro', formData.tipoRetiro);
      formDataEnvio.append('fondoCesantias', formData.fondoCesantias);
      formDataEnvio.append('valorNumeros', formData.valorNumeros);
      formDataEnvio.append('valorLetras', formData.valorLetras);
      formDataEnvio.append('motivo', formData.motivo);
      formDataEnvio.append('firmaBase64', firmaBase64);
      formDataEnvio.append('tokenBiometrico', selloBiometrico?.token || '');
      
      if (archivoSoporte) {
        formDataEnvio.append('documento_soporte', archivoSoporte);
      }

      const res = await fetch(`${API_URL}/api/th/cesantias/crear`, {
        method: 'POST',
        headers: { 
          ...(token && { Authorization: `Bearer ${token}` }) 
        },
        body: formDataEnvio
      });

      const data = await res.json();

      if (res.ok && data.success) {
        Swal.fire({ icon: 'success', title: 'Formato Radicado', text: data.message || 'La solicitud de cesantías ha sido enviada.', confirmButtonColor: '#3b82f6' });
        setLoading(false);
        if (typeof onBackToMenu === 'function') onBackToMenu();
      } else {
        throw new Error(data.message || 'Error al registrar la solicitud');
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error de Servidor', text: error.message || 'No se pudo conectar con el servidor.' });
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto bg-slate-50/80 backdrop-blur-3xl rounded-[32px] shadow-2xl border border-white font-sans text-slate-800">
      
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-300/50">
        <div>
          <h2 className="text-2xl font-black text-slate-900 drop-shadow-sm">💰 FO-TH-98: Solicitud de Cesantías</h2>
          <p className="text-slate-500 text-xs mt-1 font-medium">Diligencie este formulario para el retiro parcial o definitivo.</p>
        </div>
        <button 
          type="button" 
          onClick={onBackToMenu} 
          className="px-4 py-2 text-xs font-bold bg-white text-slate-800 rounded-full border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors shadow-sm"
        >
          Volver
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        <div className="bg-white/60 backdrop-blur-md border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row gap-6 justify-between items-center">
          <div className="w-full sm:w-1/2">
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Fecha de Solicitud:</label>
            <input type="date" name="fechaSolicitud" value={formData.fechaSolicitud} onChange={handleChange} className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" required />
          </div>
          <div className="w-full sm:w-1/2">
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tipo de Retiro:</label>
            <div className="flex items-center gap-6 bg-white border border-slate-300 rounded-xl px-4 py-2.5">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-700">
                <input type="radio" name="tipoRetiro" value="PARCIAL" checked={formData.tipoRetiro === 'PARCIAL'} onChange={handleChange} className="w-4 h-4 text-blue-600" />
                Parcial
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-700">
                <input type="radio" name="tipoRetiro" value="DEFINITIVO" checked={formData.tipoRetiro === 'DEFINITIVO'} onChange={handleChange} className="w-4 h-4 text-blue-600" />
                Definitivo
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">Información del Empleado</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase">Nombres y Apellidos</span>
              <span className="text-sm font-black text-blue-900 uppercase block mt-1">{datosEmpleado.nombres}</span>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase">Documento</span>
              <span className="text-sm font-black text-blue-900 uppercase block mt-1">{datosEmpleado.tipoDoc}. {datosEmpleado.documento}</span>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase">Centro de Trabajo</span>
              <span className="text-sm font-black text-blue-900 uppercase block mt-1">{datosEmpleado.centroTrabajo}</span>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-slate-400 uppercase">Último Cargo</span>
              <span className="text-sm font-black text-blue-900 uppercase block mt-1">{datosEmpleado.cargo}</span>
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">Información para el Retiro</h3>
          
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Fondo al que se encuentra afiliado:</label>
              <input type="text" name="fondoCesantias" value={formData.fondoCesantias} onChange={handleChange} placeholder="Ej. Porvenir, Protección..." className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-blue-500" required />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Valor a retirar (En Números):</label>
                <input type="number" name="valorNumeros" value={formData.valorNumeros} onChange={handleChange} placeholder="Ej. 1500000" className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-blue-500" required />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Valor a retirar (En Letras):</label>
                <input type="text" name="valorLetras" value={formData.valorLetras} onChange={handleChange} placeholder="Ej. Un millón quinientos mil..." className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-blue-500" required />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-3 mt-4">Motivo del Retiro:</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white p-4 rounded-xl border border-slate-300">
                <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors">
                  <input type="radio" name="motivo" value="1" checked={formData.motivo === '1'} onChange={handleChange} className="mt-1 w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-700">1. ADQUISICIÓN DE VIVIENDA O COMPRA DE LOTE</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors">
                  <input type="radio" name="motivo" value="3" checked={formData.motivo === '3'} onChange={handleChange} className="mt-1 w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-700">3. AMORTIZACIÓN CUOTAS DE VIVIENDA</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors">
                  <input type="radio" name="motivo" value="2" checked={formData.motivo === '2'} onChange={handleChange} className="mt-1 w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-700">2. REPARACIONES LOCATIVAS O CONSTRUCCIÓN LOTE PROPIO</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-2 hover:bg-slate-50 rounded-lg transition-colors">
                  <input type="radio" name="motivo" value="4" checked={formData.motivo === '4'} onChange={handleChange} className="mt-1 w-4 h-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-700">4. EDUCACIÓN BÁSICA Y SUPERIOR</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-2">Adjuntar Documento de Soporte (PDF):</label>
              <input 
                type="file" 
                accept="application/pdf" 
                onChange={(e) => setArchivoSoporte(e.target.files[0])}
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 file:mr-4 file:py-1 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" 
              />
              <span className="text-[10px] text-slate-400 mt-1 block">Suba el archivo correspondiente según el motivo seleccionado.</span>
            </div>
          </div>
        </div>

        <div className="bg-white/40 backdrop-blur-sm border border-slate-200 rounded-2xl overflow-hidden shadow-sm text-xs">
          <div className="bg-slate-200/50 p-3 text-center border-b border-slate-200">
            <h3 className="font-black text-slate-800 uppercase tracking-widest">Procedimiento de Solicitud y Anexos</h3>
          </div>
          <div className="p-4 space-y-3 text-slate-600 font-medium">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-slate-200/50 pb-2">
              <span className="font-bold text-slate-800">1. COMPRA VIVIENDA:</span>
              <span className="sm:col-span-3 text-[11px]">Fotocopia cédulas, Promesa de compra-venta, Extracto fondo, Certificado de tradición vigente (Máx 90 días).</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-slate-200/50 pb-2">
              <span className="font-bold text-slate-800">2. REPARACIONES:</span>
              <span className="sm:col-span-3 text-[11px]">Certificado de Tradición (Máx 90 días), Contrato de obra firmado, Fotos del lugar a reparar, Cédulas, Extracto fondo.</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 border-b border-slate-200/50 pb-2">
              <span className="font-bold text-slate-800">3. AMORTIZACIÓN:</span>
              <span className="sm:col-span-3 text-[11px]">Estado de cuenta vigente, Certificado tradición (Máx 90 días), Extracto fondo.</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <span className="font-bold text-slate-800">4. EDUCACIÓN:</span>
              <span className="sm:col-span-3 text-[11px]">Serán tramitados directamente por el trabajador, con el fondo correspondiente.</span>
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-slate-200 rounded-2xl p-5 shadow-sm">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">Verificación de Identidad del Solicitante</h3>
          <p className="text-[10px] text-slate-500 mb-4 font-bold">El trabajador declara bajo la gravedad de juramento que destinará las cesantías para la finalidad señalada.</p>
          
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex-1 bg-white border border-slate-300 rounded-xl p-3 relative">
              <span className="absolute top-2 left-3 text-[10px] text-slate-400 font-bold select-none pointer-events-none">Dibuje su firma aquí:</span>
              <canvas 
                ref={canvasRef}
                onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
                className="w-full h-28 border border-dashed border-slate-300 rounded-lg cursor-crosshair touch-none mt-4 bg-slate-50/50"
                width={800} height={224}
              ></canvas>
              <button type="button" onClick={clearCanvas} className="absolute bottom-4 right-4 text-[10px] font-bold bg-slate-100 px-3 py-1 rounded-md text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer">
                Limpiar Firma
              </button>
            </div>

            <div className="sm:w-64 relative flex flex-col justify-center items-center p-4 bg-white border border-slate-300 rounded-xl overflow-hidden">
              {!huellaVerificada ? (
                <>
                  <button type="button" onClick={capturarHuella} className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-slate-50 rounded-lg transition-colors p-2">
                    <img src="https://cdn-icons-png.flaticon.com/512/61/61155.png" alt="Seguridad" className="w-12 h-12 opacity-30 mb-2" />
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">Escanear Huella</span>
                    <span className="text-[9px] text-slate-400 mt-2 text-center">Sensor nativo del celular/PC</span>
                  </button>
                </>
              ) : (
                <div className="relative flex flex-col items-center justify-center w-full h-full">
                  <img src="https://cdn-icons-png.flaticon.com/512/61/61155.png" alt="Huella Verificada" className="absolute w-20 h-20 opacity-10" />
                  
                  <div className="z-10 flex flex-col items-center text-center">
                    <div className="bg-emerald-500 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest mb-1">
                      BIOMETRÍA APROBADA
                    </div>
                    <span className="text-[10px] font-bold text-slate-700">{datosEmpleado.nombres}</span>
                    <span className="text-[9px] text-slate-500 font-mono mt-1">CC: {datosEmpleado.documento}</span>
                    <span className="text-[8px] text-slate-400 mt-1">{selloBiometrico?.fecha}</span>
                    <div className="mt-2 border-t border-dashed border-slate-300 w-full pt-1">
                      <span className="text-[7px] text-emerald-600 font-mono font-bold block">TOKEN CRIPTOGRÁFICO:</span>
                      <span className="text-[8px] text-slate-600 font-mono break-all">{selloBiometrico?.token}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button 
            type="submit" 
            disabled={loading}
            className="w-full sm:w-auto px-10 py-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-black rounded-xl shadow-lg transition-transform active:scale-95 disabled:opacity-60 cursor-pointer tracking-wider"
          >
            {loading ? "PROCESANDO..." : "RADICAR FORMATO DIGITAL"}
          </button>
        </div>

      </form>
    </div>
  );
}